import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@web/core/query';
import { viewAsRepository, type ViewAsHit } from '@web/data/repositories';
import { t } from '@web/i18n';
import { Input } from '@web/ui/core/atoms';
import { useDebouncedValue } from '@web/ui/core/hooks';
import { CustomerAvatar, EmptyHint, VipBadge } from '@web/ui/core/molecules';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

/** Traveler lookup by e-mail or phone; picking a hit hands it to the page. */
export function TravelerSearch({
  orgId,
  onPick,
}: Readonly<{ orgId: string; onPick: (hit: ViewAsHit) => void }>): React.JSX.Element {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, DEBOUNCE_MS);
  const enabled = debounced.length >= MIN_QUERY_LENGTH;
  const results = useQuery({
    queryKey: queryKeys.viewAs.search(orgId, debounced),
    queryFn: () => viewAsRepository.search(orgId, debounced),
    enabled,
  });
  const hits = enabled ? (results.data ?? []) : [];
  return (
    <div className="mx-auto flex w-full max-w-form-md flex-col gap-3">
      <Input
        type="search"
        aria-label={t('viewAs.search.placeholder')}
        placeholder={t('viewAs.search.placeholder')}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
        }}
      />
      {enabled && results.isSuccess && hits.length === 0 ? (
        <EmptyHint density="compact">{t('viewAs.search.empty')}</EmptyHint>
      ) : null}
      <ul className="flex flex-col gap-1">
        {hits.map((hit) => (
          <li key={hit.id}>
            <TravelerHitButton hit={hit} onPick={onPick} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function TravelerHitButton({
  hit,
  onPick,
}: Readonly<{ hit: ViewAsHit; onPick: (hit: ViewAsHit) => void }>): React.JSX.Element {
  // Already masked by the kanban-api (`a***@x.com`, `(**) *****-1234`): shown as-is.
  const contact = hit.email ?? hit.phone;
  return (
    <button
      type="button"
      onClick={() => {
        onPick(hit);
      }}
      className="flex w-full items-center gap-3 rounded-md border border-border-soft bg-card px-3 py-2 text-left transition-colors duration-fast ease-standard hover:bg-muted focus-visible:shadow-ring-primary focus-visible:outline-none"
    >
      <CustomerAvatar name={hit.name} vip={hit.vip} size="sm" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-2 text-base font-medium text-foreground">
          <span className="truncate">{hit.name}</span>
          {hit.vip ? <VipBadge /> : null}
        </span>
        <span className="truncate text-meta text-muted-foreground">
          {[hit.companyName, contact].filter(Boolean).join(' · ')}
        </span>
      </span>
      {hit.openCards.length > 0 ? (
        <span className="shrink-0 text-meta text-muted-foreground">
          {t('viewAs.search.openCards', { count: hit.openCards.length })}
        </span>
      ) : null}
    </button>
  );
}
